import React, { useCallback, useEffect, useRef } from 'react';
import StoryScene from './scene/StoryScene';
import { usePrefersReducedMotion } from './scene/studio';
import { clamp, createTimeline, smoothstep, type StoryTimeline } from './scene/state';
import { actAnchor, heroContent, serviceById, storyActs } from '../content';
import './Story.css';

interface StoryProps {
  children?: React.ReactNode;
}

const Story: React.FC<StoryProps> = ({ children }) => {
  const reducedMotion = usePrefersReducedMotion();
  const timeline = useRef<StoryTimeline>(createTimeline());
  const sections = useRef<(HTMLElement | null)[]>([]);

  const register = useCallback(
    (index: number) => (element: HTMLElement | null) => {
      sections.current[index] = element;
    },
    [],
  );

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const viewportHeight = window.innerHeight;
      const progress = timeline.current;

      sections.current.forEach((element, index) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        progress[index].enter = clamp((viewportHeight - rect.top) / viewportHeight, 0, 1);
        progress[index].pin = clamp(-rect.top / Math.max(rect.height - viewportHeight, 1), 0, 1);
      });

      sections.current.forEach((element, index) => {
        if (!element) return;
        const next = progress[index + 1];
        const fadeIn = index === 0 ? 1 : smoothstep(progress[index].enter, 0.55, 1);
        const fadeOut = next ? 1 - smoothstep(next.enter, 0, 0.45) : 1;
        element.style.setProperty('--reveal', (fadeIn * fadeOut).toFixed(3));
      });
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  return (
    <main className="story" aria-label="How Orca Labs works">
      <div className="story-stage" aria-hidden="true">
        <StoryScene timeline={timeline} reducedMotion={reducedMotion} />
      </div>

      <div className="story-acts">
        <div className="story-act story-act--hero" id="about" ref={register(0)}>
          <div className="story-act__pin">
            <div className="story-act__inner story-hero">
              <h1 className="story-hero__title">
                {heroContent.title}
                <span className="gradient-text">{heroContent.titleGradient}</span>
              </h1>
            </div>
          </div>
        </div>

        {storyActs.map((act, index) => {
          const service = act.service ? serviceById(act.service) : undefined;
          const details = act.showServiceDetail ? service?.features : undefined;
          const anchor = actAnchor(act);

          return (
            <article
              key={act.id}
              id={anchor}
              className="story-act"
              data-act={act.id}
              ref={register(index + 1)}
            >
              {act.id === 'signals' && <span id="services" />}
              <div className="story-act__pin">
                <div className="story-act__inner">
                  <div className="story-copy">
                    <p className="story-copy__eyebrow">{act.eyebrow}</p>
                    <h2 className="story-copy__headline">{act.headline}</h2>
                    <p className="story-copy__body">{act.body}</p>
                    {details && (
                      <ul className="story-copy__details" aria-label={`${service?.title} services`}>
                        {details.map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {children && (
        <div className="story-rest" ref={register(storyActs.length + 1)}>
          {children}
        </div>
      )}
    </main>
  );
};

export default Story;
